import { useState } from "react";
import {
  Box,
  Stack,
  Button,
  Dialog,
  ButtonBase,
  IconButton,
  Typography,
  CircularProgress
} from "@material-ui/core";
import {
  Close as CloseIcon,
  ChevronLeft,
  ChevronRight,
  GetApp as DownloadIcon,
  Refresh as RetryIcon,
  AutoFixHigh as RemoveWatermarkIcon
} from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import { BeforeAfterSlider } from "components/_dashboard/makeovers/BeforeAfterSlider";

export type WatermarkComparisonImage = {
  sourceUrl: string;
  /** The watermark-free version, when one exists. */
  cleanUrl?: string;
};

type ViewMode = "before" | "both" | "after";

async function downloadImage(url: string, suffix: string): Promise<void> {
  if (!url) return;
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = `watermark-free-${suffix}-${new Date().toISOString()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank");
  }
}

type WatermarkComparisonDialogProps = {
  open: boolean;
  /** ALL the listing's images in gallery order — cleaned or not. */
  images: WatermarkComparisonImage[];
  index: number;
  /** Source URLs with an in-flight removal request (parent-owned). */
  busyUrls: Set<string>;
  onClose: () => void;
  onIndexChange: (index: number) => void;
  /**
   * Fire a removal request for one image. NOT awaited here — the parent tracks
   * busy state and the clean URL arrives via the live listing listener, so the
   * user can keep paging and queue several removals in parallel.
   */
  onRemove: (sourceUrl: string) => void;
  /**
   * Regenerate an image that already has a clean version (result wasn't good
   * enough) — replaces the stored version. Same async semantics as onRemove.
   */
  onRetry: (sourceUrl: string) => void;
};

/**
 * Full-size viewer for the watermark-removal flow, paging over ALL the listing's
 * images (arrows wrap around):
 * - Cleaned image → before / both / after modes with the makeover wipe slider + downloads.
 * - Not cleaned yet → the original, with an async "remove watermarks" button right here.
 * - Removal in progress → spinner overlay until the clean version streams in.
 */
export function WatermarkComparisonDialog({
  open,
  images,
  index,
  busyUrls,
  onClose,
  onIndexChange,
  onRemove,
  onRetry
}: WatermarkComparisonDialogProps): JSX.Element | null {
  const { t: translate } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("both");

  if (!images.length) return null;

  const safeIndex = Math.max(0, Math.min(index, images.length - 1));
  const image = images[safeIndex];
  const isBusy = busyUrls.has(image.sourceUrl);
  const hasMany = images.length > 1;

  const goTo = (i: number) => onIndexChange((i + images.length) % images.length);

  const segments: { key: ViewMode; label: string }[] = [
    { key: "before", label: translate("makeovers.before", "לפני") },
    { key: "both", label: translate("makeovers.both", "שניהם") },
    { key: "after", label: translate("makeovers.after", "אחרי") }
  ];

  const singleImageView = (url: string, label: string, labelColor: string) => (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        paddingTop: "66%",
        borderRadius: 2,
        overflow: "hidden",
        backgroundColor: "grey.200"
      }}
    >
      <Box
        component="img"
        src={url}
        alt=""
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "contain" }}
      />
      <Box
        style={{ left: 8 }}
        sx={{
          position: "absolute",
          top: 8,
          px: 1,
          py: 0.25,
          borderRadius: 1,
          backgroundColor: labelColor,
          color: "common.white"
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );

  // pointerEvents: none — purely visual, so the nav arrows keep working and the
  // user can page on while removals run in the background.
  const busyOverlay = (
    <Stack
      alignItems="center"
      justifyContent="center"
      gap={1}
      sx={{
        position: "absolute",
        inset: 0,
        borderRadius: 2,
        pointerEvents: "none",
        backgroundColor: "rgba(255, 255, 255, 0.6)"
      }}
    >
      <CircularProgress />
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {translate("watermarkRemoval.inProgress", "מסירים סימני מים…")}
      </Typography>
    </Stack>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      // stopPropagation on the PAPER only (never an onClick on the Dialog itself —
      // that would override MUI's internal backdrop-click handler and break
      // close-on-click-out): contains content clicks so they don't bubble through
      // the portal into a clickable listing card hosting this dialog.
      PaperProps={{ sx: { borderRadius: 2 }, onClick: (e) => e.stopPropagation() }}
    >
      <Box sx={{ position: "relative", p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {translate("watermarkRemoval.dialogTitle", "הסרת סימני מים")}
            {hasMany ? ` (${safeIndex + 1}/${images.length})` : ""}
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            {image.cleanUrl && (
              <Stack direction="row" gap={0.5} sx={{ p: 0.5, borderRadius: 999, backgroundColor: "grey.200" }}>
                {segments.map((s) => (
                  <ButtonBase
                    key={s.key}
                    onClick={() => setViewMode(s.key)}
                    sx={{
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 999,
                      fontSize: 13,
                      fontWeight: 600,
                      color: viewMode === s.key ? "common.white" : "grey.800",
                      backgroundColor: viewMode === s.key ? "primary.main" : "transparent",
                      transition: "all 0.2s"
                    }}
                  >
                    {s.label}
                  </ButtonBase>
                ))}
              </Stack>
            )}
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </Stack>

        <Box sx={{ position: "relative" }}>
          {image.cleanUrl ? (
            viewMode === "both" ? (
              <BeforeAfterSlider
                key={`${image.sourceUrl}|${image.cleanUrl}`}
                beforeUrl={image.sourceUrl}
                afterUrl={image.cleanUrl}
                paddingTop="66%"
                objectFit="contain"
              />
            ) : (
              singleImageView(
                viewMode === "before" ? image.sourceUrl : image.cleanUrl,
                viewMode === "before"
                  ? translate("makeovers.before", "לפני")
                  : translate("makeovers.after", "אחרי"),
                viewMode === "after" ? "primary.main" : "rgba(0,0,0,0.6)"
              )
            )
          ) : (
            singleImageView(
              image.sourceUrl,
              translate("watermarkRemoval.originalLabel", "מקור"),
              "rgba(0,0,0,0.6)"
            )
          )}

          {isBusy && busyOverlay}

          {hasMany && (
            <>
              <IconButton
                onClick={() => goTo(safeIndex - 1)}
                style={{ left: 8 }}
                sx={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  "&:hover": { backgroundColor: "common.white" }
                }}
              >
                <ChevronLeft />
              </IconButton>
              <IconButton
                onClick={() => goTo(safeIndex + 1)}
                style={{ right: 8 }}
                sx={{
                  position: "absolute",
                  top: "50%",
                  transform: "translateY(-50%)",
                  backgroundColor: "rgba(255,255,255,0.9)",
                  "&:hover": { backgroundColor: "common.white" }
                }}
              >
                <ChevronRight />
              </IconButton>
            </>
          )}
        </Box>

        {image.cleanUrl && viewMode === "both" && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block", textAlign: "center" }}>
            {translate("makeovers.explainer.dragHint", "גררו את המחוון כדי להשוות — או הקישו להחלפה")}
          </Typography>
        )}

        <Stack direction="row" alignItems="center" justifyContent="center" gap={2} sx={{ mt: 1.5 }} flexWrap="wrap">
          {image.cleanUrl ? (
            <>
              <Button
                variant="contained"
                color="primary"
                startIcon={<DownloadIcon />}
                onClick={() => downloadImage(image.cleanUrl as string, "after")}
              >
                {translate("makeovers.download", "הורדה")}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                disabled={isBusy}
                startIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : <RetryIcon />}
                onClick={() => onRetry(image.sourceUrl)}
              >
                {translate("watermarkRemoval.retry", "לא יצא טוב? נסו שוב")}
              </Button>
              <Button variant="text" size="small" onClick={() => downloadImage(image.sourceUrl, "before")}>
                {translate("makeovers.downloadBefore", "הורדת תמונת המקור")}
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              disabled={isBusy}
              startIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : <RemoveWatermarkIcon />}
              onClick={() => onRemove(image.sourceUrl)}
            >
              {isBusy
                ? translate("watermarkRemoval.inProgress", "מסירים סימני מים…")
                : translate("watermarkRemoval.removeSingle", "הסר סימני מים מתמונה זו")}
            </Button>
          )}
        </Stack>
      </Box>
    </Dialog>
  );
}

export default WatermarkComparisonDialog;
